<template>
  <div class="page-container">
    <!-- 页面头 -->
    <div class="page-head">
      <div>
        <h1>实时态势</h1>
        <p class="subtitle">{{ updatedAt }} 更新 · 仅统计有效观测数据</p>
      </div>
      <div class="head-actions">
        <el-button size="small" plain icon="el-icon-setting" @click="layoutDrawer = true">自定义模块</el-button>
        <span class="auto-refresh">
          <el-switch v-model="autoRefresh" active-text="自动刷新" @change="toggleAutoRefresh" />
        </span>
        <el-button type="primary" icon="el-icon-refresh" :loading="loading" @click="loadAll()">刷新数据</el-button>
      </div>
    </div>

    <!-- 自组模块网格：顺序与显隐由用户自定义，持久化到 localStorage -->
    <div class="dash-modules">
      <section
        v-for="(key, i) in renderList"
        :key="key"
        class="dash-module"
        :class="{ 'span-full': key === 'metrics' || key === 'trend' }"
        :draggable="dragEnabled"
        @dragstart="onDragStart(i, $event)"
        @dragenter.prevent="onDragEnter(i)"
        @dragover.prevent
        @drop.prevent="onDrop"
        @dragend="onDragEnd"
      >
        <div class="dash-module-head">
          <span
            class="drag-handle"
            title="按住拖拽调整顺序"
            @mousedown="dragEnabled = true"
            @mouseup="dragEnabled = false"
          >⠿</span>
          <h3>{{ moduleTitle(key) }}</h3>
          <div class="dash-module-actions">
            <el-button type="text" size="mini" icon="el-icon-close" @click="toggleVisible(key)">隐藏</el-button>
          </div>
        </div>
        <div class="dash-module-body">
          <!-- 模块：核心指标 -->
          <div v-if="key === 'metrics'" class="metric-grid">
            <div class="metric-card">
              <div class="metric-label">在线设备</div>
              <div class="metric-value">{{ overview.onlineDevices || 0 }} / {{ overview.totalDevices || 0 }}</div>
              <div class="metric-note">接入成功率 {{ overview.successRate || 0 }}%</div>
            </div>
            <div class="metric-card">
              <div class="metric-label">当前活动告警</div>
              <div class="metric-value">{{ overview.activeAlerts || 0 }}</div>
              <div class="metric-note">{{ (overview.seriousAlerts || 0) }} 条严重，{{ (overview.warningAlerts || 0) }} 条预警</div>
            </div>
            <div class="metric-card">
              <div class="metric-label">近 24 小时观测</div>
              <div class="metric-value">{{ overview.obs24h || 0 }}</div>
              <div class="metric-note">有效观测记录数</div>
            </div>
            <div class="metric-card">
              <div class="metric-label">当前花粉总数</div>
              <div class="metric-value">{{ overview.latestTotal || 0 }}</div>
              <div class="metric-note">最新采样 · 单位：粒 / 采样周期</div>
            </div>
          </div>

          <!-- 模块：花粉趋势 -->
          <div v-else-if="key === 'trend'">
            <div class="mod-extra">单位：粒 / 采样周期</div>
            <div ref="trendChart" class="chart-box"></div>
          </div>

          <!-- 模块：站点健康度 -->
          <div v-else-if="key === 'stations'">
            <el-table :data="stations" size="small" style="width: 100%">
              <el-table-column prop="name" label="站点" min-width="110" />
              <el-table-column label="设备" width="70">
                <template slot-scope="{ row }">{{ row.online }} / {{ row.deviceTotal }}</template>
              </el-table-column>
              <el-table-column label="状态" width="80">
                <template slot-scope="{ row }">
                  <span :class="row.status === '在线' ? 'text-success' : 'text-danger'">
                    <i class="dot" :class="row.status === '在线' ? 'online' : 'bad'"></i>{{ row.status }}
                  </span>
                </template>
              </el-table-column>
            </el-table>
            <p class="dash-note">离线设备已触发设备状态告警。</p>
          </div>

          <!-- 模块：待处置告警 -->
          <div v-else-if="key === 'alerts'">
            <el-table :data="alerts" size="small" style="width: 100%">
              <el-table-column label="等级" width="90">
                <template slot-scope="{ row }">
                  <el-tag :type="row.level === '严重' ? 'danger' : (row.level === '预警' ? 'warning' : 'info')" size="mini">
                    {{ row.level }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="content" label="告警内容" min-width="220" show-overflow-tooltip />
              <el-table-column prop="target" label="对象" min-width="160" />
              <el-table-column prop="createdTime" label="发生时间" width="160" />
              <el-table-column label="状态" width="90">
                <template slot-scope="{ row }">
                  <el-tag :type="statusType(row.status)" size="mini">{{ row.status }}</el-tag>
                </template>
              </el-table-column>
            </el-table>
            <el-empty v-if="!alerts.length" description="暂无待处置告警" :image-size="70" />
          </div>
        </div>
      </section>
    </div>

    <!-- 自定义布局抽屉 -->
    <el-drawer title="自定义模块布局" :visible.sync="layoutDrawer" size="360px" append-to-body>
      <p class="drawer-tip">在首页按住模块左上角手柄可拖拽排序；下方可开关显示或上下调整顺序，配置自动保存。</p>
      <div class="layout-list">
        <div v-for="(key, i) in order" :key="key" class="layout-item">
          <span class="layout-grip">⠿</span>
          <span class="layout-name">{{ moduleTitle(key) }}</span>
          <el-switch v-model="visible[key]" @change="onVisibleChange" />
          <div class="layout-move">
            <el-button size="mini" icon="el-icon-top" :disabled="i === 0" @click="moveItem(i, -1)" />
            <el-button size="mini" icon="el-icon-bottom" :disabled="i === order.length - 1" @click="moveItem(i, 1)" />
          </div>
        </div>
      </div>
      <div class="drawer-foot">
        <el-button type="primary" plain @click="resetLayout">恢复默认布局</el-button>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { getDashboardOverview, getDashboardTrend, getDashboardStations, getDashboardAlerts } from '@/api'

const MODULE_DEFS = [
  { key: 'metrics', title: '核心指标' },
  { key: 'trend', title: '花粉浓度趋势' },
  { key: 'stations', title: '站点健康度' },
  { key: 'alerts', title: '待处置告警' }
]
const DEFAULT_ORDER = MODULE_DEFS.map(m => m.key)
const LAYOUT_KEY = 'pollen_dashboard_layout'

export default {
  name: 'Dashboard',
  data() {
    return {
      loading: false,
      overview: {},
      stations: [],
      alerts: [],
      updatedAt: '—',
      chart: null,
      trendData: [],
      autoRefresh: true,
      refreshTimer: null,
      // 自组模块状态
      order: [...DEFAULT_ORDER],
      visible: Object.fromEntries(DEFAULT_ORDER.map(k => [k, true])),
      dragEnabled: false,
      dragIndex: -1,
      dragList: [],
      layoutDrawer: false
    }
  },
  computed: {
    moduleTitle() {
      return (key) => {
        const m = MODULE_DEFS.find(d => d.key === key)
        return m ? m.title : key
      }
    },
    // 当前可见模块的渲染顺序
    displayList() {
      return this.order.filter(k => this.visible[k])
    },
    // 拖拽期间用 dragList（可实时换位），否则用 displayList
    renderList() {
      return this.dragList.length ? this.dragList : this.displayList
    }
  },
  mounted() {
    this.loadLayout()
    this.loadAll()
    this.$bus.$on('scope-change', () => this.loadAll(true))
    this.$bus.$on('theme-change', this.onThemeChange)
    this.startAutoRefresh()
  },
  beforeDestroy() {
    this.$bus.$off('scope-change')
    this.$bus.$off('theme-change', this.onThemeChange)
    this.stopAutoRefresh()
    if (this.chart) { this.chart.dispose(); this.chart = null }
  },
  methods: {
    /* ---------------- 自组模块 ---------------- */
    loadLayout() {
      try {
        const saved = JSON.parse(localStorage.getItem(LAYOUT_KEY) || 'null')
        if (saved && Array.isArray(saved.order) && saved.visible) {
          const all = DEFAULT_ORDER
          const order = saved.order.filter(k => all.includes(k))
          all.forEach(k => { if (!order.includes(k)) order.push(k) })
          this.order = order
          this.visible = { ...Object.fromEntries(all.map(k => [k, true])), ...saved.visible }
        }
      } catch (e) { /* 读取失败则用默认布局 */ }
    },
    saveLayout() {
      localStorage.setItem(LAYOUT_KEY, JSON.stringify({ order: this.order, visible: this.visible }))
    },
    toggleVisible(key) {
      this.visible[key] = !this.visible[key]
      // 趋势图随显隐销毁/重建，避免 echarts 绑到已移除的 DOM
      if (key === 'trend') {
        if (!this.visible[key] && this.chart) {
          this.chart.dispose()
          this.chart = null
        } else if (this.visible[key]) {
          this.$nextTick(() => this.renderChart(this.trendData))
        }
      }
      this.saveLayout()
    },
    onVisibleChange() {
      this.saveLayout()
    },
    moveItem(i, dir) {
      const j = i + dir
      if (j < 0 || j >= this.order.length) return
      const arr = this.order
      const tmp = arr[i]
      this.$set(arr, i, arr[j])
      this.$set(arr, j, tmp)
      this.saveLayout()
    },
    resetLayout() {
      this.order = [...DEFAULT_ORDER]
      this.visible = Object.fromEntries(DEFAULT_ORDER.map(k => [k, true]))
      this.saveLayout()
      this.$message.success('已恢复默认布局')
    },
    /* 拖拽排序（仅对可见模块生效，最终合并回全量 order） */
    onDragStart(i, e) {
      this.dragList = [...this.displayList]
      this.dragIndex = i
      if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move'
    },
    onDragEnter(i) {
      if (this.dragIndex === -1 || this.dragIndex === i) return
      const list = this.dragList
      const moved = list.splice(this.dragIndex, 1)[0]
      list.splice(i, 0, moved)
      this.dragIndex = i
    },
    onDrop() {
      if (this.dragList.length) this.commitOrder(this.dragList)
      this.dragIndex = -1
      this.dragList = []
      this.$nextTick(() => { if (this.chart) this.chart.resize() })
    },
    onDragEnd() {
      this.dragIndex = -1
      this.dragList = []
    },
    commitOrder(visibleOrdered) {
      let vi = 0
      this.order = this.order.map(k => (this.visible[k] ? visibleOrdered[vi++] : k))
      this.saveLayout()
    },

    /* ---------------- 自动刷新 ---------------- */
    startAutoRefresh() {
      this.stopAutoRefresh()
      if (!this.autoRefresh) return
      this.refreshTimer = setInterval(() => { this.loadAll(true) }, 30000)
    },
    stopAutoRefresh() {
      if (this.refreshTimer) { clearInterval(this.refreshTimer); this.refreshTimer = null }
    },
    toggleAutoRefresh(val) {
      this.autoRefresh = val
      if (val) this.startAutoRefresh()
      else this.stopAutoRefresh()
    },

    /* ---------------- 数据加载 ---------------- */
    async loadAll(silent) {
      if (!silent) this.loading = true
      // 数据范围：把顶栏选中的区域传给后端（「全部区域」传空，由后端不过滤）
      const scope = this.$store && this.$store.state.scope
      const region = (!scope || scope === '全部区域') ? '' : scope
      try {
        const [ov, trend, st, al] = await Promise.all([
          getDashboardOverview(region), getDashboardTrend(region), getDashboardStations(region), getDashboardAlerts(region)
        ])
        this.overview = ov.data || {}
        this.stations = st.data || []
        this.alerts = (al.data || []).slice(0, 5)
        this.updatedAt = this.overview.updatedAt || '—'
        this.renderChart(trend.data || [])
      } finally {
        if (!silent) this.loading = false
      }
    },

    /* ---------------- 图表（随主题重绘） ---------------- */
    chartColors() {
      const dark = document.documentElement.getAttribute('data-theme') === 'dark'
      return {
        axis: dark ? '#3a4a60' : '#d9e2ee',
        label: dark ? '#93a1b5' : '#61728a',
        split: dark ? '#22303f' : '#eef2f8',
        line: dark ? '#4f9df7' : '#1d68c9',
        areaTop: dark ? 'rgba(79,157,247,0.28)' : 'rgba(29,104,201,0.22)',
        areaBottom: dark ? 'rgba(79,157,247,0.02)' : 'rgba(29,104,201,0.02)',
        tooltipBg: dark ? 'rgba(20,28,40,0.92)' : '#ffffff',
        tooltipBorder: dark ? '#2f3b4f' : '#d9e2ee',
        tooltipText: dark ? '#e6edf7' : '#132238'
      }
    },
    renderChart(data) {
      this.trendData = Array.isArray(data) ? data : []
      // ref 位于 v-for 内部，Vue2 会返回数组；取第一个真实 DOM 元素
      const el = Array.isArray(this.$refs.trendChart) ? this.$refs.trendChart[0] : this.$refs.trendChart
      if (!el) return  // 趋势模块被隐藏时 ref 不存在，仅缓存数据
      if (!this.chart) this.chart = echarts.init(el)
      const c = this.chartColors()
      const times = this.trendData.map(d => d.time)
      const totals = this.trendData.map(d => d.total)
      this.chart.setOption({
        grid: { left: 44, right: 20, top: 24, bottom: 30 },
        tooltip: {
          trigger: 'axis',
          backgroundColor: c.tooltipBg,
          borderColor: c.tooltipBorder,
          textStyle: { color: c.tooltipText }
        },
        axisPointer: {
          lineStyle: { color: c.axis },
          label: { backgroundColor: c.tooltipBg, borderColor: c.tooltipBorder, color: c.tooltipText }
        },
        xAxis: {
          type: 'category',
          data: times,
          boundaryGap: false,
          axisLine: { lineStyle: { color: c.axis } },
          axisLabel: { color: c.label, fontSize: 11 }
        },
        yAxis: {
          type: 'value',
          name: '粒',
          nameTextStyle: { color: c.label },
          splitLine: { lineStyle: { color: c.split } },
          axisLabel: { color: c.label, fontSize: 11 }
        },
        series: [{
          type: 'line',
          data: totals,
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: { width: 3, color: c.line },
          itemStyle: { color: c.line },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: c.areaTop },
              { offset: 1, color: c.areaBottom }
            ])
          }
        }]
      })
    },
    onThemeChange() {
      // 主题切换后重绘图表以套用新配色
      this.$nextTick(() => this.renderChart(this.trendData))
    },
    statusType(status) {
      if (status === '已关闭') return 'info'
      if (status === '处理中') return 'warning'
      return 'danger'
    }
  }
}
</script>

<style scoped>
.head-actions {
  display: flex;
  align-items: center;
  gap: 14px;
}
.auto-refresh {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  color: var(--p-muted);
}

/* 自组模块网格 */
.dash-modules {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  align-items: start;
}
.dash-module {
  background: var(--p-surface);
  border: 1px solid var(--p-line);
  border-radius: var(--p-radius);
  box-shadow: var(--p-shadow);
  overflow: hidden;
}
.dash-module.span-full { grid-column: 1 / -1; }
.dash-module-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 16px;
  border-bottom: 1px solid var(--p-line);
  background: var(--p-subtle);
}
.drag-handle {
  cursor: grab;
  color: var(--p-muted);
  font-size: 16px;
  line-height: 1;
  user-select: none;
}
.drag-handle:active { cursor: grabbing; }
.dash-module-head h3 {
  margin: 0;
  font-size: 15px;
  font-weight: 500;
  flex: 1;
  color: var(--p-ink);
}
.dash-module-actions { display: flex; align-items: center; }
.dash-module-body { padding: 16px 18px; }
.mod-extra { color: var(--p-muted); font-size: 12px; margin-bottom: 6px; }

.chart-box { width: 100%; height: 240px; }
.dash-note { color: var(--p-muted); font-size: 12px; margin: 10px 0 0; }

/* 自定义布局抽屉 */
.drawer-tip { color: var(--p-muted); font-size: 12px; line-height: 1.7; margin: 0 0 16px; }
.layout-list { display: flex; flex-direction: column; gap: 10px; }
.layout-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid var(--p-line);
  border-radius: var(--p-radius);
  background: var(--p-subtle);
}
.layout-grip { color: var(--p-muted); cursor: default; }
.layout-name { flex: 1; font-size: 14px; color: var(--p-ink); }
.layout-move { display: flex; gap: 4px; }

@media (max-width: 992px) {
  .dash-modules { grid-template-columns: 1fr; }
  .dash-module.span-full { grid-column: auto; }
}
</style>
